import { useEffect, useState } from "react";
import AdminLayout from "@/components/admin/AdminLayout";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { UserPlus, ShieldCheck, Shield, Trash2, KeyRound, Loader2 } from "lucide-react";

export default function AdminTeam() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", password: "", role: "admin" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/admins");
      setItems(r.data || []);
    } catch (e) {
      if (e.response?.status === 403) toast.error("Solo il super admin può gestire il team");
      else toast.error("Errore caricamento team");
      setItems([]);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await api.post("/admin/admins", form);
      toast.success(`${res.data.role === "superadmin" ? "Super admin" : "Admin"} creato`);
      setForm({ email: "", name: "", password: "", role: "admin" });
      setShowNew(false);
      load();
    } catch (er) { toast.error(er.response?.data?.detail || "Errore"); } finally { setSaving(false); }
  };

  const changeRole = async (id, role) => {
    try {
      await api.patch(`/admin/admins/${id}`, { role });
      toast.success("Ruolo aggiornato");
      load();
    } catch { toast.error("Errore"); }
  };

  const resetPwd = async (id) => {
    const pwd = window.prompt("Nuova password (min 8 caratteri):");
    if (!pwd || pwd.length < 8) return;
    try {
      await api.patch(`/admin/admins/${id}`, { password: pwd });
      toast.success("Password aggiornata");
    } catch { toast.error("Errore"); }
  };

  const remove = async (id, email) => {
    if (!window.confirm(`Eliminare ${email}?`)) return;
    try {
      await api.delete(`/admin/admins/${id}`);
      toast.success("Admin eliminato");
      load();
    } catch (er) { toast.error(er.response?.data?.detail || "Errore"); }
  };

  const canManage = user?.role === "superadmin";

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="font-mono-tech text-[10px] uppercase tracking-widest text-xd-muted">Sicurezza · Solo super admin</div>
          <h1 className="font-admin text-2xl font-bold">Team Admin</h1>
          <p className="text-sm text-xd-muted mt-1">Crea altri amministratori per il pannello audrivio. I superadmin controllano tutto; gli admin normali non vedono blog, homepage, recensioni e marketing.</p>
        </div>
        {canManage && (
          <button data-testid="admin-team-new-btn" onClick={() => setShowNew(true)} className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-xd-orange text-white text-sm font-semibold hover:opacity-90">
            <UserPlus className="w-4 h-4" /> Nuovo admin
          </button>
        )}
      </div>

      {loading ? (
        <div className="py-12 text-center text-xd-muted"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Caricamento…</div>
      ) : (
        <div className="bg-white rounded-xl border border-xd-border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-xd-light/60 text-[11px] font-mono-tech uppercase tracking-widest text-xd-muted">
              <tr>
                <th className="px-4 py-3 text-left">Utente</th>
                <th className="px-4 py-3 text-left">Email</th>
                <th className="px-4 py-3 text-left">Ruolo</th>
                <th className="px-4 py-3 text-right">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {items.map((u) => {
                const isSuper = u.role === "superadmin";
                const isSelf = user?.id === u.id;
                return (
                  <tr key={u.id} data-testid={`admin-row-${u.id}`} className="border-t border-xd-border">
                    <td className="px-4 py-3">
                      <div className="font-medium">{u.name}</div>
                      {isSelf && <div className="text-[10px] text-xd-cyanHover font-mono-tech uppercase">Tu</div>}
                    </td>
                    <td className="px-4 py-3">{u.email}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border ${isSuper ? "bg-xd-cyan/10 text-xd-cyanHover border-xd-cyan/30" : "bg-slate-100 text-slate-700 border-slate-200"}`}>
                        {isSuper ? <ShieldCheck className="w-3 h-3" /> : <Shield className="w-3 h-3" />}
                        {isSuper ? "Super Admin" : "Admin"}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {canManage && !isSelf ? (
                        <div className="flex flex-wrap gap-1.5 justify-end">
                          <button data-testid={`admin-role-toggle-${u.id}`} onClick={() => changeRole(u.id, isSuper ? "admin" : "superadmin")} className="h-10 px-3 text-sm rounded-lg bg-slate-100 hover:bg-slate-200 border border-slate-200 whitespace-nowrap" title="Cambia ruolo">
                            {isSuper ? "→ Admin" : "→ Super"}
                          </button>
                          <button data-testid={`admin-reset-pwd-${u.id}`} onClick={() => resetPwd(u.id)} className="h-10 px-3 text-sm rounded-lg bg-slate-100 hover:bg-slate-200 border border-slate-200 inline-flex items-center gap-1.5 whitespace-nowrap" title="Reset password">
                            <KeyRound className="w-4 h-4" /> Password
                          </button>
                          <button data-testid={`admin-delete-${u.id}`} onClick={() => remove(u.id, u.email)} className="h-10 w-10 flex items-center justify-center rounded-lg text-red-500 bg-red-50 hover:bg-red-100 border border-red-200" title="Elimina">
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                      ) : (
                        <div className="text-right text-xs text-xd-muted">{isSelf ? "—" : "solo super admin"}</div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showNew && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowNew(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={create} className="bg-white rounded-xl max-w-md w-full p-6 space-y-3" data-testid="admin-team-modal">
            <h3 className="font-display text-xl font-semibold">Crea nuovo admin</h3>
            <input required type="email" placeholder="Email di lavoro" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="admin-team-email" className="w-full h-10 px-3 rounded-lg border border-xd-border text-sm" />
            <input required placeholder="Nome e cognome" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="admin-team-name" className="w-full h-10 px-3 rounded-lg border border-xd-border text-sm" />
            <input required minLength={8} type="password" placeholder="Password (min 8)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="admin-team-password" className="w-full h-10 px-3 rounded-lg border border-xd-border text-sm" />
            <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="admin-team-role" className="w-full h-10 px-3 rounded-lg border border-xd-border text-sm">
              <option value="admin">Admin (Prodotti, Ordini, Clienti, Fornitori)</option>
              <option value="superadmin">Super Admin (accesso totale)</option>
            </select>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setShowNew(false)} className="h-10 px-4 rounded-lg border border-xd-border text-sm">Annulla</button>
              <button type="submit" disabled={saving} data-testid="admin-team-submit" className="h-10 px-4 rounded-lg bg-xd-orange text-white text-sm font-semibold disabled:opacity-60">
                {saving ? "Creazione…" : "Crea admin"}
              </button>
            </div>
          </form>
        </div>
      )}
    </AdminLayout>
  );
}
